'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn, numberFormatter } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { getMarketStatus } from '../../utils/market';
import { getMarketKey } from './marketKey';
import type { PolymarketMarket } from './types';
import { useMarketRealtimeSubscriptions } from '@/hooks/data/useMarketRealtimeSubscriptions';
import {
  type MarketRealtimeData,
  useMarketRealtimeStore,
} from '@/hooks/store/useMarketRealtimeStore';
import { useWorldCupTeamFlagStore } from '@/hooks/store/useWorldCupTeamFlagStore';
import { translateWorldCupTeamName } from '../../world-cup/teamTranslation';

const TABLE_COLUMNS = 'grid-cols-[minmax(220px,2fr)_repeat(7,1fr)]';

type SortKey = 'market' | 'status' | 'yesPrice' | 'yesChange' | 'noPrice' | 'noChange';
type SortDirection = 'asc' | 'desc';

interface MarketPriceProps {
  markets: PolymarketMarket[];
  className?: string;
}

export default function MarketPrice({ markets, className }: MarketPriceProps) {
  const { t } = useTranslation();
  const realtimeMarkets = useMarketRealtimeStore((state) => state.marketsById);
  const [sortState, setSortState] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'yesPrice',
    direction: 'desc',
  });
  const sortedMarkets = useMemo(
    () => sortMarkets(markets, realtimeMarkets, sortState),
    [markets, realtimeMarkets, sortState],
  );

  useMarketRealtimeSubscriptions(markets);
  if (!markets.length) return null;

  const handleSort = (key: SortKey) => {
    setSortState((current) => {
      if (current?.key !== key) {
        return { key, direction: getDefaultSortDirection(key) };
      }

      return {
        key,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  return (
    <section className={cn('mt-5', className)}>
      <h2 className="mb-4 text-lg font-semibold leading-6 text-white">{t('Current Prices')}</h2>
      <div className="overflow-x-auto">
        <div className="w-full min-w-208">
          <div
            className={cn(
              'grid h-10.75 items-center border-b border-[#222222] text-xs font-bold leading-4.5 text-[#666666]',
              TABLE_COLUMNS,
            )}
          >
            <HeaderCell
              sortable
              sortDirection={getSortDirection(sortState, 'market')}
              onSort={() => handleSort('market')}
            >
              {t('Market')}
            </HeaderCell>
            <HeaderCell
              sortable
              sortDirection={getSortDirection(sortState, 'status')}
              onSort={() => handleSort('status')}
            >
              {t('Status')}
            </HeaderCell>
            <HeaderCell>{t('Outcome')}</HeaderCell>
            <HeaderCell
              sortable
              sortDirection={getSortDirection(sortState, 'yesPrice')}
              onSort={() => handleSort('yesPrice')}
            >
              {t('Price')}
            </HeaderCell>
            <HeaderCell
              sortable
              sortDirection={getSortDirection(sortState, 'yesChange')}
              onSort={() => handleSort('yesChange')}
            >
              {t('24h Change')}
            </HeaderCell>
            <HeaderCell>{t('Outcome')}</HeaderCell>
            <HeaderCell
              sortable
              sortDirection={getSortDirection(sortState, 'noPrice')}
              onSort={() => handleSort('noPrice')}
            >
              {t('Price')}
            </HeaderCell>
            <HeaderCell
              sortable
              sortDirection={getSortDirection(sortState, 'noChange')}
              onSort={() => handleSort('noChange')}
            >
              {t('24h Change')}
            </HeaderCell>
          </div>

          <div>
            {sortedMarkets.map((market, index) => (
              <MarketPriceRow
                key={getMarketKey(market) || index}
                market={market}
                realtimeMarket={realtimeMarkets[market.market_id]}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeaderCell({
  children,
  sortable,
  sortDirection,
  onSort,
}: {
  children: React.ReactNode;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}) {
  const content = (
    <>
      <span className="truncate">{children}</span>
      {sortable && <SortIcon direction={sortDirection} />}
    </>
  );

  if (sortable) {
    return (
      <button
        type="button"
        className={cn(
          'flex min-w-0 items-center gap-1 text-left transition-colors hover:text-[#B7B9BC] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
          sortDirection && 'text-[#B7B9BC]',
        )}
        onClick={onSort}
      >
        {content}
      </button>
    );
  }

  return <div className="flex min-w-0 items-center gap-1">{content}</div>;
}

function SortIcon({ direction }: { direction?: SortDirection }) {
  if (direction === 'asc') {
    return <ArrowUp size={12} strokeWidth={1.7} className="shrink-0" />;
  }

  if (direction === 'desc') {
    return <ArrowDown size={12} strokeWidth={1.7} className="shrink-0" />;
  }

  return <ArrowUpDown size={12} strokeWidth={1.5} className="shrink-0 opacity-50" />;
}

function MarketPriceRow({
  market,
  realtimeMarket,
}: {
  market: PolymarketMarket;
  realtimeMarket?: MarketRealtimeData;
}) {
  const { t } = useTranslation();
  const title = getMarketTitle(market);
  const yesPrice = getYesPrice(market, realtimeMarket);
  const noPrice = getNoPrice(market, realtimeMarket);
  const yesChange = toNullableNumber(market.price_change_24h);
  const noChange = yesChange == null ? null : -yesChange;
  const status = getDisplayStatus(market, realtimeMarket);

  return (
    <div
      className={cn(
        'grid h-13.75 items-center border-b border-[#1A1A1A] text-sm leading-5 text-white last:border-b-0',
        TABLE_COLUMNS,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <MarketIcon market={market} title={title} />
        {/* <span className="truncate font-bold">{translateWorldCupTeamName(title, t)}</span> */}
        <span className="truncate font-bold">{title}</span>
      </div>
      <div>
        <span
          className={cn(
            'inline-flex h-6.5 items-center rounded-md px-2 py-1 text-xs font-bold leading-4.5',
            status === 'Active' ? 'bg-[#14C784]/6 text-[#14C784]' : 'bg-white/5 text-[#757575]',
          )}
        >
          {status}
        </span>
      </div>
      <BodyCell strong>{market.outcome_yes || 'Yes'}</BodyCell>
      <BodyCell>{formatCents(yesPrice)}</BodyCell>
      <ChangeCell value={yesChange} />
      <BodyCell strong>{market.outcome_no || 'No'}</BodyCell>
      <BodyCell>{formatCents(noPrice)}</BodyCell>
      <ChangeCell value={noChange} />
    </div>
  );
}

function BodyCell({ children, strong }: { children: React.ReactNode; strong?: boolean }) {
  return <div className={cn('truncate', strong ? 'font-bold' : 'font-semibold')}>{children}</div>;
}

function ChangeCell({ value }: { value: number | null }) {
  const direction = getChangeDirection(value);

  return (
    <div
      className={cn(
        'truncate font-semibold',
        direction === 'up' && 'text-[#14C784]',
        direction === 'down' && 'text-[#FF4D4D]',
        direction === 'flat' && 'text-[#757575]',
      )}
    >
      {formatChange(value)}
    </div>
  );
}

function MarketIcon({ market, title }: { market: PolymarketMarket; title: string }) {
  const storedFlagImage = useWorldCupTeamFlagStore((state) => state.getTeamFlagImage(title));
  const imageSrc =
    storedFlagImage ||
    market.market_icon ||
    market.market_image ||
    market.event_icon ||
    market.event_image;

  if (imageSrc) {
    return (
      <Image
        src={imageSrc}
        alt={title}
        width={24}
        height={24}
        className="size-6 shrink-0 rounded border border-[#3C3C3C] object-cover"
      />
    );
  }

  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded bg-linear-to-br from-[#14C784] to-[#0EA5E9] text-[10px] font-bold text-white">
      {getInitials(title)}
    </span>
  );
}

function sortMarkets(
  markets: PolymarketMarket[],
  realtimeMarkets: Record<string, MarketRealtimeData>,
  sortState: { key: SortKey; direction: SortDirection } | null,
) {
  if (!sortState) return markets;

  return [...markets].sort((left, right) => {
    const leftRealtime = realtimeMarkets[left.market_id];
    const rightRealtime = realtimeMarkets[right.market_id];
    const result = compareSortValues(
      getSortValue(left, leftRealtime, sortState.key),
      getSortValue(right, rightRealtime, sortState.key),
      sortState.direction,
    );

    return result;
  });
}

function getSortValue(
  market: PolymarketMarket,
  realtimeMarket: MarketRealtimeData | undefined,
  key: SortKey,
) {
  switch (key) {
    case 'market':
      return getMarketTitle(market);
    case 'status':
      return getDisplayStatus(market, realtimeMarket);
    case 'yesPrice':
      return getYesPrice(market, realtimeMarket);
    case 'yesChange':
      return toNullableNumber(market.price_change_24h);
    case 'noPrice':
      return getNoPrice(market, realtimeMarket);
    case 'noChange': {
      const yesChange = toNullableNumber(market.price_change_24h);
      return yesChange == null ? null : -yesChange;
    }
  }
}

function compareSortValues(
  left: string | number | null,
  right: string | number | null,
  direction: SortDirection,
) {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;

  if (typeof left === 'number' && typeof right === 'number') {
    const result = left - right;
    return direction === 'asc' ? result : -result;
  }

  const result = String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  return direction === 'asc' ? result : -result;
}

function getDefaultSortDirection(key: SortKey): SortDirection {
  return key === 'market' || key === 'status' ? 'asc' : 'desc';
}

function getSortDirection(
  sortState: { key: SortKey; direction: SortDirection } | null,
  key: SortKey,
) {
  return sortState?.key === key ? sortState.direction : undefined;
}

function getDisplayStatus(market: PolymarketMarket, realtimeMarket?: MarketRealtimeData) {
  return realtimeMarket?.marketClosed ? 'Ended' : getMarketStatus(market) || 'Active';
}

function getYesPrice(market: PolymarketMarket, realtimeMarket?: MarketRealtimeData) {
  return realtimeMarket?.yesPrice ?? toNullableNumber(market.outcome_yes_price);
}

function getNoPrice(market: PolymarketMarket, realtimeMarket?: MarketRealtimeData) {
  return realtimeMarket?.noPrice ?? toNullableNumber(market.outcome_no_price);
}

function getMarketTitle(market: PolymarketMarket) {
  return market.group_item_title || market.market_question || market.outcome_yes || 'Market';
}

function formatCents(value: number | null) {
  if (value == null) return '-';

  return `${numberFormatter(value * 100, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}¢`;
}

function formatChange(value: number | null) {
  if (value == null) return '-';

  const percent = normalizeZero(value * 100);
  const prefix = percent > 0 ? '+' : '';

  return `${prefix}${numberFormatter(percent, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  })}%`;
}

function getChangeDirection(value: number | null) {
  if (value == null) return 'flat';

  const normalized = normalizeZero(value);
  if (normalized > 0) return 'up';
  if (normalized < 0) return 'down';
  return 'flat';
}

function normalizeZero(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.abs(value) < 0.000001 ? 0 : value;
}

function toNullableNumber(value?: string | number | null) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function getInitials(value: string) {
  return value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}
